import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import styled from 'styled-components'

import { CategoriesModal } from '../..'
import {
  postApiCategoriesGetCategoriesList,
  type CategoriesListItemDto,
} from '../../../../shared/api/generated'
import { useQueryString } from '../../../../shared/hooks/useQueryString'
import { BaseModal } from '../../../../shared/ui-kit/actual/modals/BaseModal'
import {
  FilterTitle,
  FlexColumnStart,
  NeutralLink,
} from '../../../../shared/ui-kit/projectComponents'
import { useGetSectionsListQuery } from '../../../sectionList'
import { useCategoriesGetCategoryInfoQuery } from '../../../sectionList/queries'
import FilterByCategories from './FilterByCategories'

const SectionWrapper = styled(FlexColumnStart)`
  width: 100%;
`

const SectionTitle = styled(FilterTitle)`
  margin-bottom: 16px;
`

const ClearStyledBtn = styled.button`
  background: none;
  outline: none;
  border: none;
  margin: 0;
  padding: 0;
  color: #008c8c;
  font-weight: 700;
  font-size: 14;
  cursor: pointer;
`

const FilterByCategoriesSection = ({
  handleFilterModalOpen,
  isSearch,
}: {
  handleFilterModalOpen: () => void
  isSearch?: boolean
}) => {
  const { pathname } = useLocation()
  const route = pathname.match(/^\/sections\/(\d+)(?:\/(\d+))?(?:\/sub\/(\d+))?\/?$/)
  const sectionId = !isSearch && route ? Number(route[1]) : undefined
  const routeParentId = sectionId && route?.[2] ? Number(route[2]) : undefined
  const categoryId = sectionId && route?.[3] ? Number(route[3]) : undefined
  // Short category URLs may omit the parent; resolve it from category metadata.
  const needsParent = Boolean(categoryId && !routeParentId)
  const { data: categoryInfo, isLoading: categoryInfoLoading } = useCategoriesGetCategoryInfoQuery(
    { id: categoryId },
    { enabled: needsParent }
  )
  const parentCategoryId = routeParentId ?? categoryInfo?.parentCategoryId ?? undefined
  const { data: sectionData, isLoading: sectionsLoading } = useGetSectionsListQuery(
    {},
    { enabled: !sectionId }
  )
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['filter_sibling_categories', sectionId, parentCategoryId],
    enabled: Boolean(sectionId) && (!needsParent || Boolean(categoryInfo)),
    queryFn: async () => {
      const items: CategoriesListItemDto[] = []
      const take = 100
      let totalCount = Infinity

      while (items.length < totalCount) {
        const response = await postApiCategoriesGetCategoriesList({
          ...(parentCategoryId ? { parentCategoryId } : { sectionId }),
          skip: items.length,
          take,
        })
        const page = response.items ?? []
        items.push(...page)
        totalCount = response.totalCount ?? items.length
        if (page.length === 0) break
      }

      return items
    },
    refetchOnWindowFocus: false,
  })
  const sortingCategories = sectionId
    ? categories.map(category => ({ id: category.categoryId, name: category.categoryName }))
    : sectionData?.sections || []
  const isLoading = sectionId
    ? categoriesLoading || (needsParent && categoryInfoLoading)
    : sectionsLoading
  const title = sectionId ? 'Категории' : 'Разделы'
  const parentHref = sectionId
    ? `/sections/${sectionId}${parentCategoryId ? `/${parentCategoryId}` : ''}`
    : '/sections'

  const navigate = useNavigate()
  const [selectedSort, setSelectedSort] = useState(categoryId ? String(categoryId) : '')

  useEffect(() => {
    setSelectedSort(categoryId ? String(categoryId) : '')
  }, [categoryId])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const onOpenModalHandler = () => {
    setIsModalOpen(true)
  }
  const onCloseModalHandler = () => {
    setIsModalOpen(false)
  }
  const { setValue } = useQueryString('sectionId')
  const handleSetOrder = (value: string) => {
    setSelectedSort(value)
    if (isSearch) {
      setValue(value)
    } else if (sectionId) {
      const category = categories.find(item => String(item.categoryId) === value)
      if (!category) return
      navigate(`${parentHref}/${category.hasChildCategories ? '' : 'sub/'}${value}`)
    } else {
      navigate(`/sections/${value}`)
    }
    onCloseModalHandler()
    handleFilterModalOpen()
  }

  return (
    <SectionWrapper>
      <NeutralLink to={parentHref}>
        <SectionTitle>{title}</SectionTitle>
      </NeutralLink>
      <FilterByCategories
        handleSetOrder={handleSetOrder}
        selectedSort={selectedSort}
        setSelectedSort={setSelectedSort}
        sortingCategories={sortingCategories}
        isLoading={isLoading}
      />
      <ClearStyledBtn onClick={onOpenModalHandler}>
        {sectionId ? 'Все категории' : 'Все разделы'}
      </ClearStyledBtn>
      <BaseModal setViewModal={setIsModalOpen} viewModal={isModalOpen}>
        <CategoriesModal
          title={title}
          handleSetOrder={handleSetOrder}
          onCloseModal={onCloseModalHandler}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          sortingCategories={sortingCategories}
          isLoading={isLoading}
        />
      </BaseModal>
    </SectionWrapper>
  )
}

export default FilterByCategoriesSection
